import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, EmptyState, StatusPill, Avatar, ProgressBar } from "../../components/Atoms";
import { CategoryPie, HorizontalBars, ReachLineChart } from "../../components/Charts";
import {
  FileXls, ChartLineUp, ClipboardText, ListChecks, CaretDown, CaretRight,
  FunnelSimple, GenderIntersex, MapPin, Users, ChartLine,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientReportsPage() {
  const [data, setData]   = useState(null);   // /client/reports payload
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId]       = useState("");
  const [busy, setBusy]   = useState(null);
  const [openRow, setOpenRow] = useState({}); // { [projectId]: bool }
  const [openCreator, setOpenCreator] = useState({}); // { [`${pid}:${aid}`]: bool }

  const fetchReports = async (pid = "", tid = "") => {
    try {
      const params = new URLSearchParams();
      if (pid) params.set("project_id", pid);
      if (tid) params.set("task_id", tid);
      const qs = params.toString();
      const { data: d } = await api.get(`/client/reports${qs ? `?${qs}` : ""}`);
      setData(d);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => { fetchReports("", ""); }, []);

  // Re-fetch when filters change
  useEffect(() => { fetchReports(projectId, taskId); }, [projectId, taskId]);

  // When project changes, reset task filter
  useEffect(() => { setTaskId(""); }, [projectId]);

  const dl = async (endpoint, name) => {
    setBusy(endpoint);
    try { await downloadFile(endpoint, name); toast.success(`${name} downloaded`); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const charts  = data?.charts  || { status: [], gender: [], city: [] };
  const projects = data?.projects || [];
  const filterProjects = data?.filters?.projects || [];
  const filterTasks    = data?.filters?.tasks || [];

  const hasAnyChart = useMemo(() => (
    (charts.status?.some(s => s.value > 0)) ||
    (charts.gender?.length > 0) ||
    (charts.city?.length > 0)
  ), [charts]);

  return (
    <div className="space-y-5">
      <SectionTitle title="Reports" subtitle="Filter by project & task. Charts, creator-level progress, and Excel exports." />

      {/* Master downloads */}
      <div className="grid sm:grid-cols-1 gap-3">
        <button onClick={() => dl("/reports/progress/xlsx", "progress-report.xlsx")}
          disabled={busy === "/reports/progress/xlsx"}
          data-testid="dl-progress-xlsx"
          className="glass p-5 hover-lift text-left flex items-center gap-4 disabled:opacity-50">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center glow-cyan">
            <ListChecks size={22} weight="fill" color="white" />
          </div>
          <div className="flex-1">
            <div className="text-white font-bold">Complete Progress (.xlsx)</div>
            <div className="text-xs text-muted">Overview + task × ambassador matrix per project</div>
          </div>
          <FileXls size={20} className="text-[#A3E635]" weight="bold" />
        </button>
      </div>

      {/* Filters */}
      <div className="glass p-5">
        <div className="flex items-center gap-2 mb-3 text-white font-bold">
          <FunnelSimple size={18} weight="bold" className="text-[#22D3EE]" />
          Filters
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted font-bold mb-1">Project</div>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
              data-testid="report-filter-project"
              className="input-base text-sm">
              <option value="">All projects</option>
              {filterProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted font-bold mb-1">Task</div>
            <select value={taskId} onChange={(e) => setTaskId(e.target.value)}
              disabled={!projectId}
              data-testid="report-filter-task"
              className="input-base text-sm disabled:opacity-50">
              <option value="">{projectId ? "All tasks in this project" : "Select a project first"}</option>
              {filterTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </label>
        </div>
        {(projectId || taskId) && (
          <button onClick={() => { setProjectId(""); setTaskId(""); }}
            data-testid="clear-filters"
            className="btn-ghost text-xs mt-3">Clear filters</button>
        )}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="glass p-5" data-testid="chart-status">
          <div className="flex items-center gap-2 mb-1">
            <ListChecks size={16} weight="bold" className="text-[#A3E635]" />
            <div className="text-white font-bold">Submission Status</div>
          </div>
          <div className="text-xs text-muted mb-3">Approved / Pending / Rejected</div>
          {charts.status?.some(s => s.value > 0)
            ? <CategoryPie data={charts.status} />
            : <EmptyState title="No submissions in this scope" subtitle="Try removing filters." />}
        </div>
        <div className="glass p-5" data-testid="chart-gender">
          <div className="flex items-center gap-2 mb-1">
            <GenderIntersex size={16} weight="bold" className="text-[#EC4899]" />
            <div className="text-white font-bold">Gender Distribution</div>
          </div>
          <div className="text-xs text-muted mb-3">Creators participating</div>
          {charts.gender?.length > 0
            ? <CategoryPie data={charts.gender} />
            : <EmptyState title="No creator data" />}
        </div>
        <div className="glass p-5" data-testid="chart-city">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={16} weight="bold" className="text-[#22D3EE]" />
            <div className="text-white font-bold">Top Cities</div>
          </div>
          <div className="text-xs text-muted mb-3">By creator count</div>
          {charts.city?.length > 0
            ? <HorizontalBars data={charts.city} color="#22D3EE" />
            : <EmptyState title="No city data" />}
        </div>
      </div>

      {/* Additional charts row: Age brackets + Cumulative reach */}
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="glass p-5" data-testid="chart-age">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} weight="bold" className="text-[#F59E0B]" />
            <div className="text-white font-bold">Age Brackets</div>
          </div>
          <div className="text-xs text-muted mb-3">Creators by age (18-23 / 24-29 / 30-34 / 34-39 / 40+)</div>
          {(charts.age || []).some(x => x.value > 0)
            ? <HorizontalBars data={charts.age} color="#F59E0B" />
            : <EmptyState title="No age data yet" subtitle="Age is optional on the creator profile." />}
        </div>
        <div className="glass p-5" data-testid="chart-reach">
          <div className="flex items-center gap-2 mb-1">
            <ChartLine size={16} weight="bold" className="text-[#A3E635]" />
            <div className="text-white font-bold">Cumulative Project Reach</div>
          </div>
          <div className="text-xs text-muted mb-3">Approved submissions × creator followers, growing over time.</div>
          {(charts.reach || []).length > 0
            ? <ReachLineChart data={charts.reach} />
            : <EmptyState title="No approved submissions yet" subtitle="Reach grows as creators complete tasks." />}
        </div>
      </div>

      {/* Project Progress Table */}
      <div className="glass p-0 overflow-hidden" data-testid="project-progress-table">
        <div className="p-5 border-b border-white/5 flex items-center justify-between gap-3">
          <div>
            <div className="text-white font-bold">Project Progress</div>
            <div className="text-xs text-muted">Click a row to view creators · click a creator to see task statuses.</div>
          </div>
          {!hasAnyChart && projects.length === 0 && <div className="text-xs text-muted">No data</div>}
        </div>

        {projects.length === 0 ? (
          <EmptyState icon={ClipboardText} title="No projects in this scope" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-muted">
                  <th className="text-left px-5 py-3 font-bold w-10"></th>
                  <th className="text-left px-5 py-3 font-bold">Project</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-right px-5 py-3 font-bold">Creators</th>
                  <th className="text-right px-5 py-3 font-bold">Tasks</th>
                  <th className="text-right px-5 py-3 font-bold">Completed</th>
                  <th className="text-right px-5 py-3 font-bold">Progress</th>
                  <th className="text-right px-5 py-3 font-bold">Reward</th>
                  <th className="text-right px-5 py-3 font-bold w-32">Export</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => {
                  const isOpen = !!openRow[p.project_id];
                  return (
                    <React.Fragment key={p.project_id}>
                      <tr
                        className="border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => setOpenRow(s => ({ ...s, [p.project_id]: !s[p.project_id] }))}
                        data-testid={`project-row-${p.project_id}`}
                      >
                        <td className="px-5 py-4">
                          {isOpen
                            ? <CaretDown size={16} weight="bold" className="text-[#22D3EE]" />
                            : <CaretRight size={16} weight="bold" className="text-muted" />}
                        </td>
                        <td className="px-5 py-4 text-white font-bold">{p.title}</td>
                        <td className="px-5 py-4"><StatusPill status={p.status} /></td>
                        <td className="px-5 py-4 text-right text-white" data-testid={`row-creators-${p.project_id}`}>
                          {p.total_creators}
                        </td>
                        <td className="px-5 py-4 text-right text-white">{p.total_tasks}</td>
                        <td className="px-5 py-4 text-right text-white" data-testid={`row-completed-${p.project_id}`}>
                          <span className="text-[#A3E635] font-bold">{p.completed_creators}</span>
                          <span className="text-muted text-xs"> / {p.total_creators}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3 justify-end">
                            <div className="w-28"><ProgressBar value={p.progress_pct} max={100} color="#A3E635" /></div>
                            <span className="text-white font-bold w-12 text-right">{p.progress_pct}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right text-white">{fmtMoney(p.reward_amount)}</td>
                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); dl(`/reports/project/${p.project_id}/xlsx`, `project-${p.title}.xlsx`); }}
                            data-testid={`dl-project-xlsx-${p.project_id}`}
                            className="btn-ghost text-xs inline-flex items-center gap-1.5 text-[#A3E635]">
                            <FileXls size={14} weight="bold" /> .xlsx
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-white/3 border-t border-white/5">
                          <td colSpan={9} className="px-5 py-4">
                            {p.creators.length === 0 ? (
                              <div className="text-sm text-muted py-2">No approved creators yet for this project.</div>
                            ) : (
                              <div className="space-y-3">
                                {p.creators.map(c => {
                                  const key = `${p.project_id}:${c.ambassador_id}`;
                                  const cOpen = !!openCreator[key];
                                  return (
                                    <div key={key} className="rounded-2xl border border-white/5 bg-white/3"
                                      data-testid={`creator-row-${p.project_id}-${c.ambassador_id}`}>
                                      <div
                                        className="flex items-center gap-4 p-4 cursor-pointer rounded-2xl"
                                        onClick={() => setOpenCreator(s => ({ ...s, [key]: !s[key] }))}
                                      >
                                        {cOpen
                                          ? <CaretDown size={14} weight="bold" className="text-[#22D3EE]" />
                                          : <CaretRight size={14} weight="bold" className="text-muted" />}
                                        <Avatar src={c.photo} name={c.name} size={36} />
                                        <div className="min-w-0 flex-1">
                                          <div className="text-white text-sm font-bold truncate">{c.name}</div>
                                          <div className="text-[11px] text-muted truncate">
                                            {c.city || "—"} · {c.gender || "—"}
                                          </div>
                                        </div>
                                        <div className="hidden sm:block w-28"><ProgressBar value={c.progress_pct} max={100} color="#8B5CF6" /></div>
                                        <div className="text-right">
                                          <div className="text-white font-bold text-sm">{c.completed}/{c.total}</div>
                                          <div className="text-[10px] uppercase tracking-wider text-muted font-bold">tasks</div>
                                        </div>
                                      </div>
                                      {cOpen && (
                                        <div className="px-4 pb-4">
                                          {c.tasks.length === 0 ? (
                                            <div className="text-xs text-muted px-2 py-2">No tasks.</div>
                                          ) : (
                                            <div className="grid sm:grid-cols-2 gap-3">
                                              {c.tasks.map(t => (
                                                <div key={t.task_id} className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white/5">
                                                  <div className="min-w-0 flex-1">
                                                    <div className="text-sm text-white truncate">{t.task_title}</div>
                                                    {(t.link || t.file_url) && (
                                                      <div className="text-[11px] text-muted truncate flex items-center gap-2">
                                                        {t.link && <a href={t.link} target="_blank" rel="noreferrer" className="text-[#22D3EE] underline">link</a>}
                                                        {t.file_url && <a href={t.file_url} target="_blank" rel="noreferrer" className="text-[#A3E635] underline">file</a>}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <StatusPill status={t.status} />
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
